import cloneDeep from 'lodash/cloneDeep';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';
import {v4} from 'uuid';
import {create} from '#zustand';
import {devtools} from 'zustand/middleware';
import {createClient, LsonObject} from '@liveblocks/client';
// eslint-disable-next-line import/extensions, import/no-unresolved
import {liveblocks} from '#@liveblocks/zustand'; // eslint is not happy with scoped subpath-import
import type {WithLiveblocks} from '@liveblocks/zustand';
import {addEdge, applyEdgeChanges, applyNodeChanges, MarkerType, reconnectEdge} from '@xyflow/react';
import type {
  EdgeChange,
  NodeChange,
  OnConnect,
  OnEdgesChange,
  OnNodesChange,
  OnReconnect,
  XYPosition,
} from '@xyflow/react';

import {DateRangeType, TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {MetricDefinition} from 'common/react-query/metrics/types';
import {apiClient} from 'common/util/api-client';
import {Canvas} from 'apps/canvas/src/types';
import {BoardFilter} from 'apps/boards/src/dashboards-app/components/global-filter-bar/types';
import {navigate} from 'common/util/url';

import {getProjectId} from '../util';
import {FilterClause} from '../insights/util/types';
import {bookmarkUrlsClient} from '../queries/app-api/bookmark-urls-client';
import {ReportName} from '../constants';
import {AppEdge} from './edges/types';
import {AppNode, isMetricNode, isPureTextNode, isStickyNoteNode, isTextNode} from './nodes/types';
import {MetricNodeType} from './nodes/metric-node/types';
import {TextNodeType} from './nodes/text-node/types';
import {Cursor} from './cursor-presence/types';
import {TimeRangeAndComparison, Visibility} from './types';
import {TrackingEvent} from './tracking';
import {StickyNoteNodeType} from './nodes/sticky-note-node/types';
import {PureTextNodeType} from './nodes/pure-text-node/types';

export type AppStateGetters = {
  roomId?: string;
  canvas: null | Canvas;
  localSelectedNodeIds: string[];
  localSelectedEdgeIds: string[];
  nodes: AppNode[];
  edges: AppEdge[];
  ghostNode: null | AppNode;
  globalFilters: BoardFilter[];
  savedGlobalFilters: BoardFilter[];
  asOfDate: string; // "today" | ISO date string
  savedAsOfDate: string; // "today" | ISO date string
  timeRangesAndComparisons: TimeRangeAndComparison[];
  savedTimeRangesAndComparisons: TimeRangeAndComparison[];
  activeTimeRangeIndex: Record<string, number>; // index of selected time range for each metric node
  visibility: Record<Visibility, boolean>;
  activeMetricNodeId: null | string;
  activeEdge: null | AppEdge;
  cursor: null | Cursor;
};

type AppActions = {
  generateId: (prefix: string) => string;
  addNode: (node: AppNode) => void;
  addEdge: (edge: AppEdge) => void;
  deleteEdge: (id: string) => void;
  deleteNode: (id: string) => void;
  stageGhostNode: (type: AppNode[`type`]) => void;
  commitGhostNode: (params: {currentUserName?: string}) => void;
  updateGhostNodePosition: (position: XYPosition) => void;
  duplicateNode: (
    id: string,
    position: XYPosition,
    // all false by default
    options: {
      duplicateEdges: boolean;
      setActiveOnDuplicatedNode: boolean;
      removeEdgesFromOriginalNode: boolean;
    },
  ) => void;
  getNode: <T extends AppNode = AppNode>(id: string) => T | undefined;
  getEdge: <T extends AppEdge = AppEdge>(id: string) => T | undefined;
  updateNodeData: <T extends AppNode>(id: string, data: Partial<T[`data`]>) => T | undefined;
  setMetricNodeDefinition: (id: string, metricDefinition: MetricDefinition) => void;
  setMetricFilters: (id: string, metricFilters: FilterClause[]) => void;
  setMetricNodeName: (id: string, metricName: string) => void;
  setMetricDataGroupId: (id: string, metricDataGroupId: string) => void;
  setMetricNodeTimeRangesAndComparisons: (
    id: string,
    timeRangesAndComparisons: null | TimeRangeAndComparison[],
  ) => void;
  setTextNodeTitle: (id: string, title: string) => void;
  setTextNodeContent: (id: string, markdown: string) => void;
  setStickyNoteNodeContent: (id: string, markdown: string) => void;
  setPureTextNodeContent: (id: string, markdown: string) => void;
  onNodesChange: OnNodesChange<AppNode>;
  onEdgesChange: OnEdgesChange<AppEdge>;
  onConnect: OnConnect;
  onReconnect: OnReconnect<AppEdge>;
  setCanvas: (canvas: Canvas) => void;
  setGlobalFilters: (filters: BoardFilter[]) => void;
  setSavedGlobalFilters: (filters: BoardFilter[]) => void;
  setAsOfDate: (asOfDate: string) => void;
  setSavedAsOfDate: (asOfDate: string) => void;
  setTimeRangesAndComparisons: (timeRangesAndComparisons: TimeRangeAndComparison[]) => void;
  setSavedTimeRangesAndComparisons: (timeRangesAndComparisons: TimeRangeAndComparison[]) => void;
  setGhostNode: (ghostNode: null | AppNode) => void;
  setVisibility: (visibility: Record<Visibility, boolean>) => void;
  setActiveTimeRangeIndex: (id: string, activeTimeRange: null | TimeRangeAndComparison) => void;
  resetActiveTimeRangeIndex: () => void;
  setActiveMetricNodeId: (id: null | string) => void;
  setActiveEdge: (edge: null | AppEdge) => void;
  setLocalSelectedNodeIds: (localSelectedNodeIds: string[]) => void;
  setLocalSelectedEdgeIds: (localSelectedEdgeIds: string[]) => void;
  setCursor: (cursor: null | Cursor) => void;
  init: (roomId: string) => void;
  destroy: () => void;
  // for url sync
  syncUrlHash: () => void;
  hydrate: (urlHash: string) => void;
};

type UserInfo = {
  id: string;
  info: {
    name: string;
    email: string;
    avatar: string;
  };
};

export type AppState = WithLiveblocks<
  AppStateGetters & AppActions,
  Pick<AppStateGetters, `localSelectedNodeIds` | `localSelectedEdgeIds` | `cursor`>, // presence
  LsonObject, // storage, we don't need to use raw `getStorage` from liveblocks so leave as generic LsonObject
  UserInfo
>;

const client = createClient({
  authEndpoint: () => {
    const projectId = parseInt(getProjectId(), 10);
    return apiClient.post<{token: string}>(`/api/app/projects/${projectId}/liveblocks/auth`, {});
  },
});

// TODO move these to constants
const defaultVisibility = {
  [Visibility.METRIC_VALUE]: true,
  [Visibility.SPARKLINE]: false,
  [Visibility.CORRELATION]: false,
};

const defaultTimeRangesAndComparisons: TimeRangeAndComparison[] = [
  {
    timeRange: {
      type: DateRangeType.RelativeAfter,
      window: {
        unit: TimeUnit.Day,
        value: 30,
      },
    },
    timeComparison: {
      offset: {
        unit: TimeUnit.Month,
        value: 1,
      },
    },
    title: `MoM`,
  },
  {
    timeRange: {
      type: DateRangeType.RelativeAfter,
      window: {
        unit: TimeUnit.Month,
        value: 12,
      },
    },
    timeComparison: {
      offset: {
        unit: TimeUnit.Year,
        value: 1,
      },
    },
    title: `YoY`,
  },
];

const initialState: AppStateGetters = {
  roomId: undefined,
  canvas: null,
  localSelectedNodeIds: [],
  localSelectedEdgeIds: [],
  nodes: [],
  edges: [],
  ghostNode: null,
  globalFilters: [],
  savedGlobalFilters: [],
  asOfDate: `today`,
  savedAsOfDate: `today`,
  timeRangesAndComparisons: defaultTimeRangesAndComparisons,
  savedTimeRangesAndComparisons: defaultTimeRangesAndComparisons,
  activeTimeRangeIndex: {},
  visibility: defaultVisibility,
  activeMetricNodeId: null,
  activeEdge: null,
  cursor: null,
};

export const useCanvasStore = create<WithLiveblocks<AppState>>()(
  devtools(
    liveblocks(
      (set, get) => ({
        ...cloneDeep(initialState),

        generateId: (prefix: string) => `${prefix}${v4()}`,
        onNodesChange: (changes: Array<NodeChange<AppNode>>) => {
          const selectedNodeIds: string[] = [];
          const deSelectedNodeIds: string[] = [];
          changes.forEach((change) => {
            if (change.type === `select`) {
              if (change.selected) {
                selectedNodeIds.push(change.id);
                // If the selected node is a metric node, set it as the active metric node
                // Otherwise, set active metric node to null to close the QB
                const node = get().getNode(change.id);
                // show qb only if node definition is populated
                if (node && isMetricNode(node) && node.data.metricDefinition) {
                  get().setActiveMetricNodeId(change.id);
                } else {
                  get().setActiveMetricNodeId(null);
                }
              } else {
                deSelectedNodeIds.push(change.id);
              }
              return;
            }
            if (change.type === `remove`) {
              deSelectedNodeIds.push(change.id);
            }
          });

          if (selectedNodeIds.length > 0 || deSelectedNodeIds.length > 0) {
            const newLocalSelectedNodeIds = get()
              .localSelectedNodeIds.filter((id) => !deSelectedNodeIds.includes(id))
              .concat(selectedNodeIds);
            get().setLocalSelectedNodeIds(newLocalSelectedNodeIds);
          }

          // Only apply with non-selection changes
          const nonSelectChanges = changes.filter((change) => change.type !== `select`);
          const nodesWithoutEmptyDeSelectedText = get().nodes.filter((node) => {
            const isDeSelected = deSelectedNodeIds.includes(node.id);
            const doesNotHaveContent = (isPureTextNode(node) || isStickyNoteNode(node)) && !node.data.markdown;
            return !(isDeSelected && doesNotHaveContent);
          });

          set(
            {
              nodes: applyNodeChanges<AppNode>(nonSelectChanges, nodesWithoutEmptyDeSelectedText),
            },
            undefined,
            {
              type: `onNodesChange`,
              payload: changes,
            },
          );
        },
        onEdgesChange: (changes: Array<EdgeChange<AppEdge>>) => {
          const selectedEdgeIds: string[] = [];
          const deSelectedEdgeIds: string[] = [];
          changes.forEach((change) => {
            if (change.type === `select`) {
              if (change.selected) {
                selectedEdgeIds.push(change.id);
              } else {
                deSelectedEdgeIds.push(change.id);
              }
              // Close the QB if the edge is selected
              get().setActiveMetricNodeId(null);
              return;
            }
            if (change.type === `remove`) {
              deSelectedEdgeIds.push(change.id);
            }
          });

          if (selectedEdgeIds.length > 0 || deSelectedEdgeIds.length > 0) {
            const newLocalSelectedEdgeIds = get()
              .localSelectedEdgeIds.filter((id) => !deSelectedEdgeIds.includes(id))
              .concat(selectedEdgeIds);
            get().setLocalSelectedEdgeIds(newLocalSelectedEdgeIds);
          }
          // Only apply with non-selection changes
          const nonSelectChanges = changes.filter((change) => change.type !== `select`);

          set(
            {
              edges: applyEdgeChanges<AppEdge>(nonSelectChanges, get().edges),
            },
            undefined,
            `onEdgesChange`,
          );
        },
        onConnect: (connection) => {
          const {source, sourceHandle, target, targetHandle} = connection;
          const id = get().generateId(`edge`);
          const hasTextNode = [source, target].some((nodeId) => isTextNode(get().getNode(nodeId)!));
          const newEdge: AppEdge = {
            id,
            type: hasTextNode ? `simple` : `correlation`,
            source,
            target,
            sourceHandle,
            targetHandle,
            markerEnd: {type: MarkerType.Arrow},
            style: {strokeWidth: 2},
          };
          mixpanel.track(TrackingEvent.ConnectEdge, {
            canvas_id: get().canvas?.id,
            edge_id: id,
            source_id: source,
            target_id: target,
          });
          get().addEdge(newEdge);
        },
        // gets called after end of edge gets dragged to another source or target
        onReconnect: (oldEdge, newConnection) => {
          const edges = get().edges;
          mixpanel.track(TrackingEvent.ConnectEdge, {
            canvas_id: get().canvas?.id,
            edge_id: oldEdge.id,
            source_id: newConnection.source,
            target_id: newConnection.target,
          });
          set(
            {
              edges: reconnectEdge<AppEdge>(oldEdge, newConnection, edges, {shouldReplaceId: false}),
            },
            undefined,
            `onReconnect`,
          );
        },
        getNode: <T extends AppNode = AppNode>(id: string): T | undefined =>
          get().nodes.find((node) => node.id === id) as T | undefined,
        getEdge: <T extends AppEdge = AppEdge>(id: string): T | undefined =>
          get().edges.find((edge) => edge.id === id) as T | undefined,
        setGhostNode: (ghostNode) => set({ghostNode}, undefined, `setGhostNode`),
        updateNodeData: <T extends AppNode>(id: string, data: Partial<T[`data`]>) => {
          const ghostNodeId = get().ghostNode?.id;
          set(
            {
              ghostNode: ghostNodeId === id ? null : get().ghostNode,
              nodes: get().nodes.map((node) => {
                if (id === node.id) {
                  // type guard to avoid lint errors
                  return {
                    ...node,
                    data: {
                      ...node.data,
                      ...data,
                    },
                  } as T;
                }
                return node;
              }),
            },
            undefined,
            `setNodeData`,
          );
          return get().getNode<T>(id);
        },
        stageGhostNode: (type) => {
          get().setActiveMetricNodeId(null);
          const id = get().generateId(`node-`);
          const position = get().cursor || {x: 0, y: 0};
          // Initialize ghost node
          if (type === `metric`) {
            const newGhostNode: MetricNodeType = {
              id,
              type: `metric`,
              position,
              measured: {
                width: 256,
                height: 265,
              },
              data: {
                metricDefinition: null,
                metricFilters: [],
              },
            };
            get().setGhostNode(newGhostNode); // Trigger initial render
          } else if (type === `text`) {
            const newGhostNode: TextNodeType = {
              id,
              type: `text`,
              position,
              measured: {
                width: 302,
                height: 125,
              },
              data: {
                title: ``,
                markdown: ``,
              },
            };
            get().setGhostNode(newGhostNode); // Trigger initial render
          } else if (type === `stickyNote`) {
            const newGhostNode: StickyNoteNodeType = {
              id,
              type: `stickyNote`,
              position,
              measured: {
                width: 178,
                height: 127,
              },
              data: {
                author: ``,
                markdown: ``,
                lastModified: ``,
              },
            };
            get().setGhostNode(newGhostNode); // Trigger initial render
          } else if (type === `pureText`) {
            const newGhostNode: PureTextNodeType = {
              id,
              type: `pureText`,
              position,
              measured: {
                width: 70,
                height: 45,
              },
              data: {
                markdown: ``,
              },
            };
            get().setGhostNode(newGhostNode); // Trigger initial render
          }
        },
        commitGhostNode: ({currentUserName}) => {
          const ghostNode = get().ghostNode;
          // Add a new node at the transformed position
          if (!ghostNode) {
            return;
          }
          const position = get().cursor || {x: 0, y: 0};
          if (isMetricNode(ghostNode)) {
            const newNode: MetricNodeType = {
              ...ghostNode,
              selected: true,
              position,
            };
            get().addNode(newNode);
          } else if (isTextNode(ghostNode)) {
            const newNode: TextNodeType = {
              ...ghostNode,
              position,
              selected: true,
            };
            get().addNode(newNode);
          } else if (isStickyNoteNode(ghostNode)) {
            const newNode: StickyNoteNodeType = {
              // keep the id stable so it re-render but not re-mount
              ...ghostNode,
              position,
              selected: true,
              data: {
                author: currentUserName ?? ``,
                markdown: ``,
                lastModified: new Date().toISOString(),
              },
            };
            get().addNode(newNode);
          } else if (isPureTextNode(ghostNode)) {
            const newNode: PureTextNodeType = {
              ...ghostNode,
              position,
              selected: true,
            };
            get().addNode(newNode);
          }
          get().setGhostNode(null); // we can remove the ghost node
        },
        updateGhostNodePosition: (position) => {
          const ghostNode = get().ghostNode;
          if (!ghostNode) return;
          get().setGhostNode({...ghostNode, position});
        },
        init: (roomId: string) => {
          set({roomId}, undefined, `init`);
          get().liveblocks.enterRoom(roomId);
          mixpanel.track(TrackingEvent.EnterRoom, {
            canvas_id: get().canvas?.id,
            room_id: roomId,
          });
        },
        destroy: () => {
          const othersCount = get().liveblocks.others.length;
          const roomId = get().liveblocks.room?.id;
          mixpanel.track(TrackingEvent.LeaveRoom, {
            canvas_id: get().canvas?.id,
            room_id: roomId,
            'number of people in room': othersCount,
          });
          get().liveblocks.leaveRoom();
          set(cloneDeep(initialState), undefined, `destroy`);
        },
        addNode: (node: AppNode) => {
          set(
            {
              nodes: [...get().nodes, node],
            },
            undefined,
            `addNode`,
          );
          mixpanel.track(TrackingEvent.AddNode, {
            canvas_id: get().canvas?.id,
            node_id: node.id,
            node_type: node.type,
            node_data: node.data,
          });
        },
        addEdge: (edge: AppEdge) => {
          set(
            {
              edges: addEdge(edge, get().edges),
            },
            undefined,
            `addEdge`,
          );
        },
        deleteEdge: (id: string) => {
          set(
            {
              edges: get().edges.filter((edge) => edge.id !== id),
            },
            undefined,
            `deleteEdge`,
          );
        },
        deleteNode: (id: string) => {
          set(
            {
              nodes: get().nodes.filter((node) => node.id !== id),
              // Delete edges connected to the deleted node. Otherwise, it will just orphan the edges
              edges: get().edges.filter((edge) => edge.source !== id && edge.target !== id),
            },
            undefined,
            `deleteNode`,
          );
        },
        /**
         * Duplication of a node -
         *
         * On overflow menu "duplicate" action, we set the duplicated node
         * to be in "active" state -> qb menu pops up
         * New node is set to an offset on the left of original node
         * usage: duplicateNode(id, {x: offsetX, y: offsetY}, {
         *   removeEdgesFromOriginalNode: false,
         *   setActiveOnDuplicatedNode: true,
         *   duplicateEdges: true,
         * })
         *
         * When doing alt + dragging on canvas, we duplicate original node at original position
         * So the user has this delusion of the node being dragged is a new duplicated node
         * But it's just a hack to smooth the experience. The node being dragged is always the original node
         * usage: duplicateNode(id, {x: originalX, y: originalY}, {
         *   setActiveOnDuplicatedNode: false,
         *   duplicateEdges: true,
         *   removeEdgesFromOriginalNode: true,
         * })
         */
        duplicateNode: (id, position, {setActiveOnDuplicatedNode, duplicateEdges, removeEdgesFromOriginalNode}) => {
          // Create the duplicated node (apply offset position to avoid overlap)
          const generateId = get().generateId;
          const node = get().getNode(id)!;
          const newNodeId = generateId(`node-`);
          const newNode: AppNode = isMetricNode(node)
            ? {
                id: newNodeId,
                type: `metric`,
                position,
                selected: false,
                data: {
                  ...(node.data.metricName ? {metricName: `${node.data.metricName} (copy)`} : {}),
                  metricDataGroupId: node.data.metricDataGroupId,
                  metricDefinition: node.data.metricDefinition,
                  metricFilters: node.data.metricFilters,
                },
              }
            : {
                ...node,
                id: newNodeId,
                selected: false,
                position,
              };

          // Update nodes and edges and set active metric node to the duplciated node
          get().addNode(newNode);
          if (duplicateEdges) {
            // Find connected edges
            const connectedEdges = get().edges.filter((edge) => edge.source === node.id || edge.target === node.id);

            // Create the duplicated edges
            const newEdges = connectedEdges.map((edge) => ({
              ...edge,
              id: generateId(`edge`),
              source: edge.source === id ? newNodeId : edge.source,
              target: edge.target === id ? newNodeId : edge.target,
            }));
            newEdges.forEach((edge) => get().addEdge(edge));
          }

          if (removeEdgesFromOriginalNode) {
            const connectedEdges = get().edges.filter((edge) => edge.source === node.id || edge.target === node.id);
            connectedEdges.forEach((edge) => {
              get().deleteEdge(edge.id);
            });
          }

          if (isMetricNode(node) && setActiveOnDuplicatedNode) {
            get().setActiveMetricNodeId(newNodeId);
          }
        },
        // metric node
        setMetricNodeDefinition: (id, metricDefinition) => {
          const {canvas, updateNodeData} = get();
          updateNodeData<MetricNodeType>(id, {metricDefinition});
          mixpanel.track(TrackingEvent.ChangeMetricDefinition, {
            canvas_id: canvas?.id,
            node_id: id,
            'metric definition': metricDefinition,
          });
        },
        setMetricFilters: (id, metricFilters) => {
          const {canvas, updateNodeData} = get();
          updateNodeData<MetricNodeType>(id, {metricFilters});
          mixpanel.track(TrackingEvent.ChangeMetricFilters, {
            canvas_id: canvas?.id,
            node_id: id,
            'metric filters': metricFilters,
          });
        },
        setMetricDataGroupId: (id, metricDataGroupId) => {
          const {canvas, updateNodeData} = get();
          updateNodeData<MetricNodeType>(id, {metricDataGroupId});
          mixpanel.track(TrackingEvent.ChangeMetricDataGroupId, {
            canvas_id: canvas?.id,
            node_id: id,
            'data group id': metricDataGroupId,
          });
        },
        setMetricNodeName: (id, metricName) => {
          const {canvas, updateNodeData} = get();
          updateNodeData<MetricNodeType>(id, {metricName});
          mixpanel.track(TrackingEvent.EditMetricTitle, {
            canvas_id: canvas?.id,
            node_id: id,
            'metric node title': metricName,
          });
        },
        setMetricNodeTimeRangesAndComparisons: (id, timeRangesAndComparisons) => {
          const {canvas, updateNodeData} = get();
          updateNodeData<MetricNodeType>(id, {timeRangesAndComparisons});
          mixpanel.track(TrackingEvent.ChangeMetricTimeRangeAndComparison, {
            canvas_id: canvas?.id,
            node_id: id,
            'time ranges and comparisons': timeRangesAndComparisons,
          });
        },
        // text node
        setTextNodeTitle: (id, title) => {
          const {canvas, updateNodeData} = get();
          updateNodeData<TextNodeType>(id, {title});
          mixpanel.track(TrackingEvent.EditTextTitle, {canvas_id: canvas?.id, node_id: id, 'text node title': title});
        },
        setTextNodeContent: debounce((id, markdown) => {
          const {canvas, updateNodeData} = get();
          updateNodeData<TextNodeType>(id, {markdown});
          mixpanel.track(TrackingEvent.EditTextContent, {
            canvas_id: canvas?.id,
            node_id: id,
            'text node content': markdown,
          });
        }, 300),
        // sticky note node
        setStickyNoteNodeContent: debounce((id, markdown) => {
          const {canvas, updateNodeData} = get();
          const today = new Date().toISOString();
          updateNodeData<StickyNoteNodeType>(id, {markdown, lastModified: today});
          mixpanel.track(TrackingEvent.EditStickyNoteContent, {
            canvas_id: canvas?.id,
            node_id: id,
            'note node content': markdown,
          });
        }, 300),
        // pure text node
        setPureTextNodeContent: debounce((id, markdown) => {
          const {canvas, updateNodeData} = get();
          updateNodeData<PureTextNodeType>(id, {markdown});
          mixpanel.track(TrackingEvent.EditPureTextNodeContent, {
            canvas_id: canvas?.id,
            node_id: id,
            'note node content': markdown,
          });
        }, 300),
        setCanvas: (canvas) => set({canvas}),
        setGlobalFilters: (globalFilters) => {
          const {canvas} = get();
          set({globalFilters}, undefined, `setGlobalFilters`);
          get().syncUrlHash();
          mixpanel.track(TrackingEvent.ChangeGlobalFilters, {canvas_id: canvas?.id, 'global filters': globalFilters});
        },
        setSavedGlobalFilters: (savedGlobalFilters) => {
          const {canvas} = get();
          set({savedGlobalFilters}, undefined, `setSavedGlobalFilters`);
          mixpanel.track(TrackingEvent.SaveGlobalFilters, {
            canvas_id: canvas?.id,
            'global filters': savedGlobalFilters,
          });
        },
        setAsOfDate: (asOfDate) => {
          const {canvas} = get();
          set({asOfDate}, undefined, `setAsOfDate`);
          get().syncUrlHash();
          mixpanel.track(TrackingEvent.ChangeAsOfDate, {canvas_id: canvas?.id, 'as of date': asOfDate});
        },
        setSavedAsOfDate: (savedAsOfDate) => {
          const {canvas} = get();
          set({savedAsOfDate}, undefined, `setSavedAsOfDate`);
          mixpanel.track(TrackingEvent.SaveAsOfDate, {canvas_id: canvas?.id, 'as of date': savedAsOfDate});
        },
        setTimeRangesAndComparisons: (timeRangesAndComparisons) => {
          const {canvas} = get();
          set({timeRangesAndComparisons});
          get().syncUrlHash();
          mixpanel.track(TrackingEvent.ChangeTimeRangesAndComparisons, {
            canvas_id: canvas?.id,
            'time ranges and comparisons': timeRangesAndComparisons,
          });
        },
        setSavedTimeRangesAndComparisons: (savedTimeRangesAndComparisons) => {
          const {canvas} = get();
          set({savedTimeRangesAndComparisons});
          mixpanel.track(TrackingEvent.SaveTimeRangesAndComparisons, {
            canvas_id: canvas?.id,
            'time ranges and comparisons': savedTimeRangesAndComparisons,
          });
        },
        setVisibility: (visibility) => {
          const {canvas} = get();
          set({visibility}, undefined, `setVisibility`);
          get().syncUrlHash();
          mixpanel.track(TrackingEvent.UpdateVisibility, {
            canvas_id: canvas?.id,
            visibility,
          });
        },
        setActiveMetricNodeId: (id) => {
          set(
            {
              activeMetricNodeId: id,
            },
            undefined,
            `setActiveMetricNodeId`,
          );
          get().syncUrlHash();
        },
        setActiveEdge: (edge) => {
          set({activeEdge: edge}, undefined, `setActiveEdge`);
        },
        setActiveTimeRangeIndex: (id, activeTimeRange) => {
          const newActiveTimeRangeIndex = {...get().activeTimeRangeIndex};
          const metricNodes = get().nodes.filter((node) => isMetricNode(node));
          if (!activeTimeRange) {
            // if no active time range, set index to 0
            newActiveTimeRangeIndex[id] = 0;
          } else {
            // For all metric nodes, find the index of the active time range and set it to the newActiveTimeRangeIndex
            // This is to ensure that the time range is set to the same index for all metric nodes that have
            // the same time range and comparison
            metricNodes.forEach((node) => {
              const timeRangeAndComparisons = node.data.timeRangesAndComparisons || get().timeRangesAndComparisons;
              const matchingTimeRange = timeRangeAndComparisons.findIndex(
                (timeRangeAndComparison) =>
                  isEqual(timeRangeAndComparison.timeRange, activeTimeRange.timeRange) &&
                  isEqual(timeRangeAndComparison.timeComparison, activeTimeRange.timeComparison),
              );
              if (matchingTimeRange !== -1) {
                newActiveTimeRangeIndex[node.id] = matchingTimeRange;
              }
            });
          }
          set({activeTimeRangeIndex: newActiveTimeRangeIndex}, undefined, `setActiveTimeRangeIndex`);
        },
        resetActiveTimeRangeIndex: () => {
          set({activeTimeRangeIndex: {}});
        },
        setLocalSelectedNodeIds: (localSelectedNodeIds) => {
          set({localSelectedNodeIds}, undefined, `setLocalSelectedNodeIds`);
        },
        setLocalSelectedEdgeIds: (localSelectedEdgeIds) => {
          set({localSelectedEdgeIds}, undefined, `setLocalSelectedEdgeIds`);
        },
        setCursor: (cursor: Cursor | null) => set({cursor}, undefined, `setCursor`),

        syncUrlHash: () => {
          const {
            nodes,
            edges,
            ghostNode,
            globalFilters,
            savedGlobalFilters,
            asOfDate,
            savedAsOfDate,
            timeRangesAndComparisons,
            savedTimeRangesAndComparisons,
            visibility,
            activeMetricNodeId,
          } = get();
          const slug = bookmarkUrlsClient.getBookmarkUrl({
            project_id: parseInt(getProjectId(), 10),
            type: ReportName.Canvas,
            params: {
              nodes,
              edges,
              savedGlobalFilters,
              savedAsOfDate,
              savedTimeRangesAndComparisons,
              visibility,
            },
            overrides: {
              ghostNode,
              globalFilters,
              asOfDate,
              timeRangesAndComparisons,
              activeMetricNodeId,
            },
          });
          let search: string | undefined;
          if (activeMetricNodeId) {
            const searchParams = new URLSearchParams();
            searchParams.set(`active-node`, activeMetricNodeId);
            search = searchParams.toString();
          }
          navigate({hash: slug, search});
        },
        // for url hydration
        hydrate: async (urlHash: string) => {
          if (urlHash) {
            const {overrides} = await bookmarkUrlsClient.fetchBookmarkUrl({
              projectId: parseInt(getProjectId(), 10),
              slug: urlHash,
            });
            const {
              ghostNode,
              globalFilters,
              asOfDate = `today`,
              timeRangesAndComparisons = defaultTimeRangesAndComparisons,
            } = overrides as Pick<AppState, `ghostNode` | `globalFilters` | `asOfDate` | `timeRangesAndComparisons`>;
            set(
              {
                ghostNode,
                globalFilters,
                asOfDate,
                timeRangesAndComparisons,
              },
              undefined,
              `hydrate`,
            );
          }
        },
      }),
      {
        client,
        storageMapping: {
          nodes: true,
          edges: true,
          savedGlobalFilters: true,
          savedAsOfDate: true,
          savedTimeRangesAndComparisons: true,
          visibility: true,
        },
        presenceMapping: {
          cursor: true,
          // we need those 2 to be in presence, because we need to highlight the color of the users who selected node / edges
          localSelectedNodeIds: true,
          localSelectedEdgeIds: true,
        },
      },
    ),
    {
      name: `canvas`,
      enabled: process.env.NODE_ENV !== `production`,
    },
  ),
);
